import VendorVariantContext from '@context/VendorVariant/vendor-variant-context';
import formatPrice from '@utils/shared/format/formatPrice';
import classNames from 'classnames';
import React, { useContext, useMemo } from 'react';
import { isBackordered, isInStock } from '@models/VendorVariant/i-vendor-variant';
import styles from './Price.module.scss';

interface IPriceProps {
  className?: string;
}

const Price = ({
  className = undefined,
}: IPriceProps) => {
  const vendorVariant = useContext(VendorVariantContext);

  const {
    availability,
    retailPrice,
    currentPrice,
  } = vendorVariant;

  const isOnSale = useMemo(() => {
    if (retailPrice && currentPrice) {
      return Number(retailPrice.price) > Number(currentPrice);
    }
    return false;
  }, [currentPrice, retailPrice]);

  const inStock = useMemo(() => isInStock(availability), [availability]);
  const backOrdered = useMemo(() => isBackordered(availability), [availability]);

  return (
    <div className={classNames(styles.PriceWrapper, className)}>
      {inStock ? (
        <div className={styles.PriceStatusWrapper}>
          <div className={styles.Price}>
            <span
              className={classNames({ [styles.IsOnSale]: isOnSale })}
              data-test={`price${isOnSale ? ' sale' : ''}`}
            >
              {formatPrice(currentPrice, false)}
            </span>
            {
              isOnSale && retailPrice?.price && (
                <StatusTag
                  statusText={formatPrice(retailPrice!.price, false) ?? ''}
                  classname={styles.Strikethrough}
                  dataTest={'price strikethrough'}
                />
              )
            }
          </div>

          {
            backOrdered && (
              <StatusTag
                statusText={'Back ordered'}
                classname={styles.BackOrdered}
                dataTest={'price backordered'}
              />
            )
          }
        </div>
      ) : (
        <StatusTag
          statusText={'Out of stock'}
          classname={styles.OutOfStock}
          dataTest={'availability'}
        />
      )}
    </div>
  );
};

interface IStatusTag {
  statusText: string;
  classname: string;
  dataTest: string;
}

function StatusTag({
  statusText,
  classname,
  dataTest,
}: IStatusTag) {
  return (
    <span className={classname} data-test={dataTest}>
      {statusText}
    </span>
  );
}

export default Price;
